// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SecurityManagerStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { action } from 'mobx';
import tenantManagementDataFetcher from 'fetchers/Izenda.TenantManagementDataFetcher';
import SystemSettingFetcher from 'fetchers/Izenda.SystemSettingFetcher';
import AuthorizationStore from 'logic/Izenda.AuthorizationStore';
import AccountStore from 'logic/Izenda.AccountStore';
import LicenseInfoStore from 'logic/Izenda.LicenseInfoStore';
import IntegrationSettingStore from 'logic/Izenda.IntegrationSettingStore';
import SecurityDetail from 'logic/models/Izenda.SecurityDetail';
import UiState from './Izenda.UiState';
import AppStore from 'logic/Izenda.AppStore';
import AuthUtils from 'share/utils/Izenda.AuthUtils';

class SecurityManagerStore {
  constructor() {
    this.authorizationStore = new AuthorizationStore();
    this.accountStore = new AccountStore();
    this.licenseInfoStore = LicenseInfoStore;
    this.integrationSettingStore = new IntegrationSettingStore();
  }

  /**
   * Fetch data
   */
  @action('fetchData')
  fetchData() {
    if (!SecurityDetail.isLoaded) {
      const licenseInfoStore = this.licenseInfoStore;
      const tenantId = AuthUtils.getSelectedTenant();
      let allowUpdateTenantId = true;

      return Promise.join(
        this.accountStore.loadCurrentUser(),
        licenseInfoStore.getLicenseInfo(),
        this.integrationSettingStore.getDeploymentMode(),
        SystemSettingFetcher.getSystemMode(),
        action((userInfo: any, licenseInfo: any, integrationSetting: any, systemMode: number) => {
          UiState.setCurrentUser(userInfo.data);
          UiState.setSystemMode(systemMode);
          if (!userInfo.data.systemAdmin) {
            AuthUtils.removeSelectedTenant();
            allowUpdateTenantId = false;
          }
          UiState.updateLicenseStatus(licenseInfo.expired);
          licenseInfoStore.licenseInfo.readLicenseInforation(licenseInfo);
          this.integrationSettingStore.integrationSetting.readDeploymentMode(integrationSetting);
          return Promise.join(
            this.authorizationStore.loadAllPermissions(),
            tenantManagementDataFetcher.loadTenant(UiState.tenantId),
            action((permission: any, tenantDetail: any) => {
              SecurityDetail.populate(permission, tenantDetail, true);
              allowUpdateTenantId && tenantId && UiState.setTenantId(tenantId);
              return Promise.resolve();
            })
          ).then(AppStore.loadAppSettings.bind(AppStore));
        }).bind(this))
        .then(licenseInfoStore.getLicenseStatus.bind(licenseInfoStore));
    }
  };

  /**
   * Check access permission on report/dashboard.
   * @param  {Object} props
   * @param  {String} id
   * @param  {Function} checkAccessFn
   */
  checkAccessPage(props: any, id: string, checkAccessFn: Function) {
    return this.authorizationStore.loadAccessPriority(id).then((result) => {
      if (!checkAccessFn) {
        return Promise.resolve(true);
      }

      const canAccessPage = checkAccessFn(result);
      if (!canAccessPage) {
        props.router.push('NoPermission');
      }

      return Promise.resolve(canAccessPage);
    });
  };
}

export default SecurityManagerStore;



// WEBPACK FOOTER //
// ./ui/logic/Izenda.SecurityManagerStore.js